/**
 * useAuth
 *
 * Reads JWT token from URL query string (?token=...) passed by HubSpot iframe.
 * No API login call needed — token is already signed by HubSpot.
 * Token payload contains: { iat, exp, data: { portalId, hsUserId, hsUserEmail } }
 *
 * Returns:
 *   authReady  - true immediately after token is extracted from URL
 *   authError  - error message string if token extraction failed
 *   credentials - { portalId, hsUserId, hsUserEmail } decoded from JWT
 *   token - the JWT string itself
 */

import { useState, useEffect } from 'react';
import { setAuthToken, getAuthToken, isAuthenticated } from '../utils/authToken';
import { loginUser } from '../api/authApi';

/**
 * Decode JWT payload (middle segment, base64url-decoded).
 * Does NOT verify signature — assumes HubSpot has already signed it.
 */
function decodeJWT(tokenString) {
  try {
    const parts = tokenString.split('.');
    if (parts.length !== 3) throw new Error('Invalid JWT format');

    const payload = parts[1];
    // Base64url decode
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (err) {
   
    return null;
  }
}

/**
 * Extract token from URL query and validate it.
 * Returns { token, credentials, expiresIn, issuedAt } or null if not found/invalid.
 */
function extractTokenFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const tokenString = params.get('token');

    if (!tokenString) {

      return null;
    }

    const payload = decodeJWT(tokenString);
    if (!payload) return null;

    const { exp, iat, data } = payload;
    if (!exp || !data) {
      
      return null;
    }

    const { portalId, hsUserId, hsUserEmail } = data;
    const expiresIn = exp - (iat || Math.floor(Date.now() / 1000));
    const issuedAt = iat || Math.floor(Date.now() / 1000);

    return {
      token: tokenString,
      credentials: { portalId, hsUserId, hsUserEmail },
      expiresIn,
      issuedAt,
    };
  } catch (err) {
   
    return null;
  }
}

/**
 * Dev credentials for local development.
 * When no token in URL, login with these credentials to get a real token from the backend.
 */
const DEV_CREDENTIALS = {
  portalId: 147537570,
  hsUserId: 27519911,
  hsUserEmail: 'vayud@transfunnel.com',
};

export function useAuth() {
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function authenticate() {
      try {
        // Try to extract token from URL first (production mode via HubSpot iframe)
        let extracted = extractTokenFromURL();
        
        if (!extracted) {
          // No token in URL — check if we're in development mode
          if (process.env.NODE_ENV === 'development') {
           
            // Call login API to get a real token
            await loginUser(DEV_CREDENTIALS);
            // loginUser internally calls setAuthToken, so we just retrieve it
            setCredentials(DEV_CREDENTIALS);
          } else {
            // Production mode without token — authentication failed
            throw new Error('Authentication token not found. Please ensure you are accessing this page through HubSpot.');
          }
        } else {
          
          // Token extracted from URL — store it and set credentials
          setAuthToken(extracted.token, extracted.expiresIn, extracted.issuedAt);
          setCredentials(extracted.credentials);
        }

        if (!cancelled) {
          setAuthReady(true);
          setAuthError(null);
        }
      } catch (err) {
        if (!cancelled) {
        
          setAuthError(err.message || 'Authentication failed');
          setAuthReady(true);
        }
      }
    }

    authenticate();
    return () => { cancelled = true; };
  }, []);

  return { authReady, authError, credentials, token: getAuthToken() };
}
